/**
 * Standalone (non-project) ad-hoc custom line quote email helpers.
 * Payload shape for POST /quotes/request_ad_hoc_quote_emails/.
 */

import { emailRegex } from '../config_and_helpers/config';
import {
  buildAdHocPayload,
  buildAdHocPayloadRow,
  validateAdHocPayload,
} from './projectAdHocPayload';

/** Lab contact source for a standalone quote send. */
export const LAB_CONTACT_MODE = {
  SAVED: 'saved',
  ONE_TIME: 'oneTime',
};

const CONTACT_NAME_MAX_LENGTH = 255;

/**
 * Default empty one-time lab contact form state.
 *
 * @returns {{ contactName: string, contactEmail: string }}
 */
export function emptyOneTimeLabContact() {
  return { contactName: '', contactEmail: '' };
}

/**
 * Map API line to supplier key for grouping preview.
 *
 * @param {Object} line
 * @returns {{ supplierKey: string, supplierId: *|null, supplierName: string }}
 */
function getStandaloneLineSupplierMeta(line) {
  const supplierId = line?.line_supplier;
  const supplierName = (line?.line_supplier_name || '').trim() || 'Unknown supplier';
  const supplierKey =
    supplierId != null && supplierId !== ''
      ? String(supplierId)
      : `name:${supplierName}`;
  return { supplierKey, supplierId: supplierId ?? null, supplierName };
}

/**
 * Build API payload lines from ad-hoc form rows.
 *
 * @param {Array<Object>} formRows - {@link emptyAdHocFormRow} shape
 * @returns {Array<Object>}
 */
export function buildStandaloneAdHocQuoteEmailLines(formRows) {
  return buildAdHocPayload(formRows).map(row => {
    const qty = Number(row.quantity_needed ?? row.quantity);
    return {
      line_name: row.line_name,
      line_cat_num: row.line_cat_num || '',
      line_supplier: row.line_supplier,
      line_supplier_name: row.line_supplier_name || '',
      line_price: row.line_price,
      line_currency: row.line_currency,
      quantity: Number.isInteger(qty) && qty >= 1 ? qty : 1,
      notes: row.notes || '',
    };
  });
}

/**
 * Validate lines before send (same rules as project ad-hoc save).
 *
 * @param {Array<Object>} lines - Output of {@link buildStandaloneAdHocQuoteEmailLines}
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateStandaloneAdHocQuoteLines(lines) {
  if (!Array.isArray(lines) || lines.length === 0) {
    return { valid: false, message: 'Add at least one custom line with a name.' };
  }
  const asProjectPayload = lines.map(line => ({
    line_name: line.line_name,
    line_supplier: line.line_supplier,
    line_supplier_name: line.line_supplier_name,
  }));
  return validateAdHocPayload(asProjectPayload);
}

/**
 * Group standalone API lines by supplier for UI preview.
 *
 * @param {Array<Object>} lines
 * @returns {Array<{ supplierKey: string, supplierId: *, supplierName: string, lines: Object[] }>}
 */
export function groupStandaloneLinesBySupplier(lines) {
  const map = new Map();
  (Array.isArray(lines) ? lines : []).forEach(line => {
    const { supplierKey, supplierId, supplierName } = getStandaloneLineSupplierMeta(line);
    if (!map.has(supplierKey)) {
      map.set(supplierKey, {
        supplierKey,
        supplierId,
        supplierName,
        lines: [],
      });
    }
    map.get(supplierKey).lines.push(line);
  });
  return [...map.values()].sort((a, b) =>
    a.supplierName.localeCompare(b.supplierName)
  );
}

/**
 * Build preview lines from form rows (non-empty names only).
 *
 * @param {Array<Object>} formRows
 * @returns {Array<Object>}
 */
export function buildStandalonePreviewLinesFromForm(formRows) {
  return (formRows || [])
    .filter(row => (row.line_name || '').trim())
    .map(row => {
      const built = buildAdHocPayloadRow(row);
      const qty = Number(row.quantity_needed);
      return {
        line_name: built.line_name,
        line_cat_num: built.line_cat_num,
        line_supplier: built.line_supplier,
        line_supplier_name: built.line_supplier_name,
        line_price: built.line_price,
        line_currency: built.line_currency,
        quantity: Number.isInteger(qty) && qty >= 1 ? qty : 1,
        notes: built.notes,
      };
    });
}

/**
 * Validate one-time lab contact fields.
 *
 * @param {{ contactName?: string, contactEmail?: string }} oneTimeContact
 * @returns {{ valid: boolean, message?: string, fieldErrors?: { contact_name?: string, contact_email?: string } }}
 */
export function validateOneTimeLabContact(oneTimeContact) {
  const fieldErrors = {};
  const name = (oneTimeContact?.contactName || '').trim();
  const email = (oneTimeContact?.contactEmail || '').trim();

  if (!name) {
    fieldErrors.contact_name = 'Contact name is required.';
  } else if (name.length > CONTACT_NAME_MAX_LENGTH) {
    fieldErrors.contact_name = `Contact name must be at most ${CONTACT_NAME_MAX_LENGTH} characters.`;
  }

  if (!email) {
    fieldErrors.contact_email = 'Contact email is required.';
  } else if (!emailRegex.test(email)) {
    fieldErrors.contact_email = 'Contact email must be a valid email address.';
  }

  if (Object.keys(fieldErrors).length > 0) {
    return {
      valid: false,
      message: 'Please enter a valid one-time lab contact name and email.',
      fieldErrors,
    };
  }
  return { valid: true };
}

/**
 * Build optional contact_name / contact_email for the quote request payload.
 *
 * @param {string} mode - {@link LAB_CONTACT_MODE}
 * @param {{ contactName?: string, contactEmail?: string }} oneTimeContact
 * @returns {{ contact_name?: string, contact_email?: string }}
 */
export function buildStandaloneQuoteRequestContactPayload(mode, oneTimeContact) {
  if (mode !== LAB_CONTACT_MODE.ONE_TIME) {
    return {};
  }
  return {
    contact_name: (oneTimeContact?.contactName || '').trim(),
    contact_email: (oneTimeContact?.contactEmail || '').trim(),
  };
}

/**
 * Whether the standalone quote send button should be enabled (before line validation).
 *
 * @param {Object} params
 * @param {string} params.mode - {@link LAB_CONTACT_MODE}
 * @param {boolean} params.hasGlobalContact
 * @param {boolean} params.isLoadingGlobalContact
 * @param {string} params.globalContactLoadError
 * @param {{ contactName?: string, contactEmail?: string }} params.oneTimeContact
 * @returns {{ canSend: boolean, disabledReason: string }}
 */
export function canSendStandaloneQuote({
  mode,
  hasGlobalContact,
  isLoadingGlobalContact,
  globalContactLoadError,
  oneTimeContact,
}) {
  if (isLoadingGlobalContact) {
    return { canSend: false, disabledReason: 'Loading lab contact settings...' };
  }
  if (globalContactLoadError) {
    return {
      canSend: false,
      disabledReason:
        'Quote requests are disabled because lab contact settings could not be loaded.',
    };
  }

  if (mode === LAB_CONTACT_MODE.ONE_TIME) {
    const validation = validateOneTimeLabContact(oneTimeContact);
    if (!validation.valid) {
      return {
        canSend: false,
        disabledReason:
          'Enter a one-time lab contact name and email, or switch to saved lab contact.',
      };
    }
    return { canSend: true, disabledReason: '' };
  }

  if (!hasGlobalContact) {
    return {
      canSend: false,
      disabledReason:
        'Quote requests are disabled until a lab contact is configured, or use a one-time lab contact.',
    };
  }
  return { canSend: true, disabledReason: '' };
}

/**
 * Default lab contact mode when global settings load.
 *
 * @param {boolean} hasGlobalContact
 * @returns {string}
 */
export function defaultLabContactMode(hasGlobalContact) {
  return hasGlobalContact ? LAB_CONTACT_MODE.SAVED : LAB_CONTACT_MODE.ONE_TIME;
}
